import { Paper, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useValueObserver } from '@superdispatch/hooks';
import { TextBox } from '@superdispatch/ui-lab';
import { useField, useFormikContext } from 'formik';
import { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import {
  mapVehicleType,
  VALID_VEHICLE_TYPES,
  VehicleModelDTO,
} from '../../../data/VehicleDTO';
import { useVehicleModels } from '../../../data/VehiclesAPI';
import useDebounce from '../../../hooks/useDebounce';
import { focusNextInput } from './VehicleMakeAutocompleteField';

const StyledPaper = styled(Paper)`
  & .MuiAutocomplete-groupLabel {
    color: #5b6371;
    font-weight: 400;
    line-height: 20px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  & .MuiListSubheader-sticky {
    position: static;
  }
`;

interface VehicleModelAutocompleteFieldProps {
  name: string;
  make: string | null;
  makeId?: number;
  disabled?: boolean;
}

export function VehicleModelAutocompleteField({
  name,
  make,
  makeId,
  disabled,
}: VehicleModelAutocompleteFieldProps) {
  const { isSubmitting, setFieldValue } = useFormikContext();
  const [{ value, onBlur }, { error, touched }, { setValue }] = useField<
    string | null
  >({ name: `${name}.model` });
  const debouncedQuery = useDebounce(value?.trim(), 300);
  const { data = [] } = useVehicleModels(debouncedQuery, makeId);
  const errorText = touched && error;
  const options = data.sort(
    (a, b) => Number(b.is_popular) - Number(a.is_popular),
  );

  useValueObserver(make, () => {
    setValue(null);
  });

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur(event.target.value);
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setFieldValue(`${name}.type`, '');
  };

  const getOptionLabel = (option: string | VehicleModelDTO) => {
    if (typeof option === 'string') {
      return option;
    }
    return option.name;
  };

  const groupBy = (option: string | VehicleModelDTO) => {
    if (typeof option === 'string') return '';

    return option.is_popular ? 'Popular Models' : 'Other Models';
  };

  const handleChange = (nextValue: string | VehicleModelDTO) => {
    const model =
      typeof nextValue !== 'string'
        ? nextValue
        : options.find(
            (option) => option.name.toLowerCase() === nextValue.toLowerCase(),
          );

    if (!model) return;

    setValue(model.name);
    if (model.type && VALID_VEHICLE_TYPES.includes(model.type)) {
      setFieldValue(`${name}.type`, mapVehicleType(model.type));
    }
    focusNextInput(`${name}.model`);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      focusNextInput(`${name}.model`);
    }
  };

  return (
    <Autocomplete
      loading={!data}
      options={options}
      openOnFocus={true}
      freeSolo={true}
      forcePopupIcon={false}
      multiple={false}
      groupBy={groupBy}
      selectOnFocus={true}
      disableClearable={true}
      value={value || ''}
      disabled={isSubmitting || disabled}
      getOptionLabel={getOptionLabel}
      onBlur={handleBlur}
      onInput={handleInput}
      onChange={(_, value) => {
        handleChange(value);
      }}
      onKeyDown={handleKeyDown}
      PaperComponent={StyledPaper}
      renderInput={(params) => (
        <TextField
          {...params}
          name={`${name}.model`}
          error={!!errorText}
          helperText={errorText}
          label={
            <>
              Model <TextBox color="secondary">(Required)</TextBox>
            </>
          }
        />
      )}
    />
  );
}
